import * as d3 from 'd3';

import {Translator} from 'src/components/TranslationsContext';
import {VizConfig, makeConfigItems, makeWeatherForecastItems} from 'src/components/Viz/config';
import {
  RateVolumeMode,
  Source,
  TimeAggregation,
  WeatherColumns,
  WeatherHistoricalDailyColumns,
} from 'src/lib/api/series';
import COLORS from 'src/lib/colors';
import {WEATHER_SOURCES} from 'src/lib/sourceConfigs';

const CLIMATOLOGY_COLUMNS_BY_GROUP = {
  precipitation: {
    mean: WeatherHistoricalDailyColumns.PRECIPITATION_MEAN,
    band: [
      WeatherHistoricalDailyColumns.PRECIPITATION_0_05,
      WeatherHistoricalDailyColumns.PRECIPITATION_0_95,
    ],
  },
  temperature: {
    mean: WeatherHistoricalDailyColumns.TEMPERATURE_MEAN,
    band: [
      WeatherHistoricalDailyColumns.TEMPERATURE_0_05,
      WeatherHistoricalDailyColumns.TEMPERATURE_0_95,
    ],
  },
} as const;

/**
 * The chart config for H5 weather: one precipitation and one temperature time series per forecast
 * source, in the legacy weather chart's order and style, so an H5 section looks like the legacy
 * one it replaces. Each source's observations, and its climatology's daily mean and 5–95% band,
 * draw behind the forecasts in the legacy chart's observed style.
 */
export function makeH5WeatherVizConfig({
  sources,
  observations = [],
  climatologies = [],
  timeAggregation,
  rateVolumeMode = 'rate',
  t,
}: {
  sources: Source[];
  // Each observation's and climatology's chart source, and the weather source name its legend
  // entries carry.
  observations?: {source: Source; name: string}[];
  climatologies?: {source: Source; name: string}[];
  timeAggregation: TimeAggregation;
  rateVolumeMode?: RateVolumeMode;
  t: Translator;
}): VizConfig {
  const curveFactory = timeAggregation === '1H' ? d3.curveLinear : d3.curveStepAfter;
  const sortedSources = [...sources].sort(
    (a, b) => WEATHER_SOURCES.indexOf(a) - WEATHER_SOURCES.indexOf(b)
  );
  // A climatology's precipitation quantiles are of a daily rate, which don't sum to the quantiles
  // of a depth, so in the depth modes the precipitation climatology is left out.
  const climatologyGroups =
    rateVolumeMode === 'rate'
      ? (['precipitation', 'temperature'] as const)
      : (['temperature'] as const);
  return {
    forecasts: makeConfigItems(
      'forecasts',
      sortedSources.flatMap((source) => makeWeatherForecastItems(source, curveFactory, t))
    ),
    observations: makeConfigItems('observations', [
      ...observations.flatMap(({source, name}) => [
        {
          displayName: `${name} ${t('observed')}`,
          source,
          columns: [WeatherColumns.PRECIPITATION],
          getStyle: () => ({stroke: COLORS.OBS, fill: COLORS.OBS, fillOpacity: 0.1}),
          legendIcon: 'rect' as const,
          curveFactory,
          groupId: t('precipitation'),
        },
        {
          displayName: `${name} ${t('observed')}`,
          source,
          columns: [WeatherColumns.TEMPERATURE],
          getStyle: () => ({stroke: COLORS.OBS}),
          legendIcon: 'line' as const,
          curveFactory,
          groupId: t('temperature'),
        },
      ]),
      // Daily, whatever the chart's aggregation, so drawn as steps.
      ...climatologies.flatMap(({source, name}) =>
        climatologyGroups.flatMap((group) => [
          {
            displayName: `${name} ${t('historicalRange5To95')}`,
            source,
            columns: [...CLIMATOLOGY_COLUMNS_BY_GROUP[group].band],
            getStyle: () => ({fill: COLORS.OBS, fillOpacity: 0.12}),
            legendIcon: 'rect' as const,
            curveFactory: d3.curveStepAfter,
            groupId: t(group),
          },
          {
            displayName: `${name} ${t('historicalMean')}`,
            source,
            columns: [CLIMATOLOGY_COLUMNS_BY_GROUP[group].mean],
            getStyle: () => ({stroke: COLORS.OBS, strokeDasharray: 2}),
            legendIcon: 'line' as const,
            curveFactory: d3.curveStepAfter,
            groupId: t(group),
          },
        ])
      ),
    ]),
  };
}
